import type { FamilyMemberApi } from "../services/api/family";
import type {
  ChildFeeAccount,
  ChildSchoolFees,
  ParentChild,
} from "../types/parentFamily";
import { currentAcademicYear } from "./schoolFeesHelpers";

function normalizeFeeAccount(raw: ChildFeeAccount): ChildFeeAccount {
  return {
    ...raw,
    id: raw.id || `fee-${Date.now()}`,
    currency: raw.currency?.trim() || "ZAR",
    payments: Array.isArray(raw.payments) ? raw.payments : [],
    paidInFull: raw.paidInFull === true,
    billingPattern:
      raw.billingPattern === "recurring-fixed" ? "recurring-fixed" : "once-off",
    category:
      raw.category === "extracurricular" || raw.category === "event"
        ? raw.category
        : "other",
  };
}

export function normalizeFeeAccounts(
  feeAccounts?: ChildFeeAccount[] | null
): ChildFeeAccount[] {
  if (!Array.isArray(feeAccounts)) return [];
  return feeAccounts.map(normalizeFeeAccount);
}

export function mapFamilyMemberFromApi(member: FamilyMemberApi): ParentChild {
  return {
    id: member.memberId,
    firstName: member.firstName,
    lastName: member.lastName,
    dateOfBirth: member.dateOfBirth,
    gender: member.gender,
    grade: member.grade,
    classSection: member.classSection,
    studentId: member.studentId,
    notes: member.notes,
    status: member.status === "inactive" ? "inactive" : "active",
    linkedCircleIds: Array.isArray(member.linkedCircleIds)
      ? member.linkedCircleIds
      : [],
    schoolFees: member.schoolFees ?? undefined,
    feeAccounts: normalizeFeeAccounts(member.feeAccounts),
    extracurriculars: Array.isArray(member.extracurriculars)
      ? member.extracurriculars
      : [],
    reportCards: Array.isArray(member.reportCards) ? member.reportCards : [],
    createdAt: member.createdAt,
    updatedAt: member.updatedAt,
    createdByUserId: member.createdByUserId,
    guardians: Array.isArray(member.guardians) ? member.guardians : [],
    myRole: member.myRole,
  };
}

export function toFamilyMemberWriteBody(child: ParentChild) {
  return {
    firstName: child.firstName,
    lastName: child.lastName,
    dateOfBirth: child.dateOfBirth,
    gender: child.gender,
    grade: child.grade,
    classSection: child.classSection,
    studentId: child.studentId,
    notes: child.notes,
    status: child.status,
    linkedCircleIds: child.linkedCircleIds,
    schoolFees: child.schoolFees,
    feeAccounts: child.feeAccounts,
    extracurriculars: child.extracurriculars,
    reportCards: child.reportCards || [],
  };
}

/** Best-effort normalization when reading API payloads. */
export function normalizeSchoolFees(
  schoolFees?: ChildSchoolFees | null
): ChildSchoolFees | undefined {
  if (!schoolFees) return undefined;
  if (
    schoolFees.totalAnnualFee == null &&
    !schoolFees.academicYear?.trim()
  ) {
    return undefined;
  }
  return {
    ...schoolFees,
    id:
      schoolFees.id ||
      `fees-${schoolFees.academicYear || "unknown"}-${schoolFees.totalAnnualFee ?? 0}`,
    academicYear: schoolFees.academicYear || currentAcademicYear(),
    currency: schoolFees.currency?.trim() || "ZAR",
    paymentPlanMonths: schoolFees.paymentPlanMonths || 12,
    payments: Array.isArray(schoolFees.payments) ? schoolFees.payments : [],
    feesPaidInFull: schoolFees.feesPaidInFull === true,
  };
}

export function mergeSchoolFeesFromPatch(
  fromApi: ChildSchoolFees | null | undefined,
  sent: ChildSchoolFees | undefined
): ChildSchoolFees | undefined {
  const apiNormalized = normalizeSchoolFees(fromApi ?? undefined);
  const sentNormalized = normalizeSchoolFees(sent);
  if (!apiNormalized && !sentNormalized) return undefined;
  if (!apiNormalized) return sentNormalized;
  if (!sentNormalized) return apiNormalized;
  const apiPayments = apiNormalized.payments ?? [];
  const sentPayments = sentNormalized.payments ?? [];
  return {
    ...apiNormalized,
    payments: apiPayments.length > 0 ? apiPayments : sentPayments,
  };
}
